import { h, Fragment } from 'preact';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, FreeMode, Pagination ,Navigation} from 'swiper';
import { useEffect, useMemo, useRef } from 'preact/hooks';
import Media from 'utils/media';
import ArrowIcon from 'utils/icon/icon-arrow.svg';

const ProductGalleryMobile = ({ currentProduct }) => {
  if (!currentProduct) return null;
  const { media } = currentProduct;
  const slider = useRef(null);

  const paginationConfig = useMemo(
    () => ({
      clickable: false,
      horizontalClass: 'mx-[18px] bg-[#dcdad4] h-[2px] mt-[11px] !flex !static !w-[calc(100%-36px)]',
      bulletClass: 'h-full flex-grow',
      bulletActiveClass: 'bg-black',
    }), []);

  const handleClick = (index) => {
    const body = document.querySelector('body');
    if (index != null) {
      body.classList.add('overflow-hidden');
    } else {
      body.classList.remove('overflow-hidden');
    }
  };

  const setVideoThumbnailVisibility = ( visibility ) => {
    const videoThumbnail = document.querySelector('[data-target="slider-video-button"]');
    if (!videoThumbnail) return;
    if (visibility) videoThumbnail.classList.remove('hidden');
    else videoThumbnail.classList.add('hidden');
  }

  const sliderToVideo = () => {
    slider.current.slideTo(slider.current.slides.length - 2);
  };

  const setLabelsAndHidden = (swiper) => {
  // count real slides (ignoring loop clones)
  const real = Array.from(swiper.slides).filter(
    (el) => !el.classList.contains('swiper-slide-duplicate')
  );
  const total = real.length || swiper.slides.length;

  swiper.slides.forEach((el, i) => {
    const raw = el.getAttribute('data-swiper-slide-index');
    const pos = raw !== null ? Number(raw) : i;

    // all slides have base role
    el.setAttribute('role', 'group');
    el.setAttribute('aria-roledescription', 'slide');

    // mark only the active one visible/current
    const isActive = i === swiper.activeIndex;
    el.setAttribute('aria-hidden', isActive ? 'false' : 'true');

    const base = `Slide ${pos + 1} of ${total}`;
    el.setAttribute('aria-label', isActive ? `Current ${base}` : base);

    if (isActive) el.setAttribute('aria-current', 'true');
    else el.removeAttribute('aria-current');
  });
};


  useEffect(() => {
    if (slider.current) {
      if (media?.length > 1) {
        slider.current.enable();
        slider.current.slideTo(1);
        slider.current.pagination.enable();
        setTimeout(() => setLabelsAndHidden(slider.current), 0);
      } else {
        slider.current.disable();
        slider.current.pagination.disable();
      }
    }
  }, [media]);

  return (
    <>
      <div className="w-full overflow-hidden relative"
        role="region"
        aria-roledescription="carousel"
        aria-label="Image gallery">
        <Swiper
          modules={[Pagination, FreeMode, Navigation, A11y]}
          loop={true}
          freeMode={false}
          pagination={paginationConfig}
          a11y= {{
            enabled:true,
            slideLabelMessage: 'Slide {{index}} of {{slidesLength}}',
            itemRoleDescriptionMessage:'slide',
          }}
        onInit={(swiper) => {
           slider.current = swiper;
            setLabelsAndHidden(swiper);
         }}
         onLoopFix={(swiper) => {
          setLabelsAndHidden(swiper);
        }}
          onSlideChange={(swiper) => {
            setVideoThumbnailVisibility(swiper.realIndex !==
              (swiper.slides.length - 3));
            setLabelsAndHidden(swiper);
          }}
        >
          {media?.length > 0 && media.map((item, index) => (
            <SwiperSlide key={item.id}>
              <div key={item.previewImage.id} className="relative pb-[100%] aaaa">
                <Media
                  {...item}
                  onClick={() => handleClick(index)}
                  className="absolute inset-0 object-cover h-full w-full"
                  controlClass="text-black bg-white !opacity-100"
                  lazyLoad={index !== 0}
                  srcWidths={{
                    sm: 640,
                    md: 768,
                    lg: 1024,
                    xl: 1280,
                    '2xl': 1536,
                  }}
                />
              </div>
            </SwiperSlide>
          ))}

          <button type="button" className='hidden text-black bg-white absolute h-10 w-10  p-2 rotate-180 left-5 inset-y-2/4 z-10 rounded-full drop-shadow-md md:flex items-center justify-center' onClick={() => slider.current.slidePrev()}>
            <span className='sr-only'>Previous slide</span>
            <ArrowIcon />
          </button>
          <button type="button" className='hidden text-black bg-white absolute h-10 w-10 p-2 right-5 inset-y-2/4 z-10 rounded-full drop-shadow-md md:flex items-center justify-center' onClick={() => slider.current.slideNext()}>
            <span className='sr-only'>Next slide</span>
            <ArrowIcon />
          </button>
        </Swiper>
        {media?.length > 0 && media[media.length - 1]?.mediaContentType === 'VIDEO' && (
          <div className="absolute w-[35px] h-[35px] right-2 top-[calc(100vw-44px)] z-10" data-target="slider-video-button">
            <Media
              {...media[media.length - 1]}
              onClick={() => sliderToVideo()}
              className="absolute inset-0 object-cover h-full w-full"
              parentClass="h-full w-full"
              controlClass="pointer-events-none !opacity-100 !left-[3px] !top-[3px] z-10 absolute w-7 h-7 rounded-full flex items-center justify-center "
              justPlayButton="true"
              srcWidths={{
                sm: 640,
                md: 768,
                lg: 1024,
                xl: 1280,
                '2xl': 1536,
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default ProductGalleryMobile;
